import { CheckSquare, FolderInput, FolderPlus, FolderOpen } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { ThemeStats } from "@/components/theme/ThemeStats";
import { formatError, useAuth } from "@/auth";
import { AllTasksBoard } from "@/components/projects/AllTasksBoard";
import { BasecampImport } from "@/components/projects/BasecampImport";
import { Avatar, timeAgo } from "@/components/projects/common";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import "@/projects.css";

function NewProject({ onClose }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", description: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/projects", form); navigate(`/projets/${data.id}?tab=membres`); } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="pj-dialog" data-testid="project-create-dialog">
        <DialogTitle className="pj-dialog-title">Nouveau projet</DialogTitle>
        <form className="pj-form" onSubmit={submit}>
          <input placeholder="Nom du projet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="project-create-name" />
          <textarea rows={3} placeholder="Description (facultatif)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-create-description" />
          <button type="submit" className="primary-button" data-testid="project-create-submit">Créer et inviter des personnes</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [params, setParams] = useSearchParams();
  const vue = params.get("vue") || localStorage.getItem("pj-home") || "projets";
  const setVue = (v) => { localStorage.setItem("pj-home", v); setParams({ vue: v }); };
  const load = useCallback(() => api.get("/projects", { params: { archived } }).then(({ data }) => setItems(data)).catch(() => setItems([])), [archived]);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="page pj-page" data-testid="projects-page">
      <header className="page-heading">
        <div><p className="eyebrow">COLLABORATION</p><h1>Projets d'équipe</h1><p className="page-subtitle">Discussions, to-do, documents et fichiers partagés avec votre équipe et vos partenaires.</p></div>
        {items && <ThemeStats stats={[
          { icon: FolderOpen, value: items.length, label: archived ? "Projets archivés" : "Projets actifs", testid: "projects-head-count" },
          { icon: CheckSquare, value: items.reduce((n, p) => n + (p.open_todos || 0), 0), label: "Tâches ouvertes", tone: "pine", testid: "projects-head-todos" },
        ]} />}
        <div className="pj-head-side">
          {user.role !== "guest" && <button type="button" className="primary-button" onClick={() => setCreating(true)} data-testid="project-new-btn"><FolderPlus size={16} />Nouveau projet</button>}
        </div>
      </header>
      <div className="pj-seg pj-mb pj-home-seg" data-testid="projects-home-switch">
        <button type="button" className={vue === "projets" ? "is-on" : ""} onClick={() => setVue("projets")} data-testid="projects-home-projects">Projets</button>
        <button type="button" className={vue === "taches" ? "is-on" : ""} onClick={() => setVue("taches")} data-testid="projects-home-tasks"><CheckSquare size={14} />Toutes les tâches</button>
      </div>
      {vue === "taches" ? <AllTasksBoard /> : (<>
      <div className="pj-seg pj-mb">
        <button type="button" className={!archived ? "is-on" : ""} onClick={() => setArchived(false)} data-testid="projects-filter-active">En cours</button>
        <button type="button" className={archived ? "is-on" : ""} onClick={() => setArchived(true)} data-testid="projects-filter-archived">Archivés</button>
      </div>
      {items?.length === 0 && <p className="pj-empty" data-testid="projects-empty">{archived ? "Aucun projet archivé." : "Aucun projet pour l'instant."}</p>}
      <div className="pj-grid">
        {items?.map((p, i) => (
          <button type="button" key={p.id} className="pj-tile" style={{ animationDelay: `${i * 40}ms` }} onClick={() => navigate(`/projets/${p.id}`)} data-testid={`project-card-${p.id}`}>
            <span className="crm-pj__top"><span className={`av av--md av--t${p.name.length % 4}`} aria-hidden="true">{p.name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("")}</span><span className="crm-pj__todo"><CheckSquare size={13} />{p.open_todos} à faire</span></span>
            <h3>{p.name}</h3>
            <p>{p.description || "Pas de description"}</p>
            <footer>
              <span className="pj-avatars">{p.member_names.slice(0, 5).map((n) => <Avatar key={n} name={n} size={26} />)}</span>
              <span className="pj-meta"><span><CheckSquare size={13} />{p.open_todos}</span><small>{timeAgo(p.last_activity_at)}</small></span>
            </footer>
          </button>
        ))}
      </div>
      </>)}
      {creating && <NewProject onClose={() => setCreating(false)} />}
      {importing && <BasecampImport onClose={() => setImporting(false)} onImported={load} />}
    </section>
  );
}
